/**
 * V181: 인사관리 > 휴가설정(휴가 마스터) 페이지
 *
 * leaveTypes 컬렉션 CRUD UI
 * - 목록: 코드, 이름, 차감유형, 승인플로우, 활성여부
 * - 생성/수정: 모달 폼
 * - 삭제: 사용 이력 없는 경우만 가능, 아니면 비활성화 유도
 *
 * 권한: HR 또는 관리자만 접근 가능 (ROUTE_PERMISSIONS에서 제어)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Search, X, RefreshCw } from 'lucide-react';
import { LeaveType, DeductionType, ApprovalFlowType, LeaveUnit } from '../../shared/types/leaveType';
import { DEFAULT_ALLOWED_UNITS } from '../../shared/constants/leaveUnits';  // V282-1
import { useLeaveTypeMutations, LeaveTypeInput, UpdateLeaveTypeInput } from '../../shared/hooks/useLeaveTypeMutations';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';

// V68: 호출자 권한 기준 노출 범위 옵션 (BE getAllowedVisibilityScopes 와 동일 정책).
//   system_admin / role_global → KR/CN/COMMON
//   role_admin_kr               → KR/COMMON
//   role_admin_cn               → CN/COMMON
//   role_hr/role_exec           → companyCode 기준 (KR ⇒ KR/COMMON, CN ⇒ CN/COMMON)
function computeAllowedScopesFE(roles: string[], companyCode?: string): Array<'KR' | 'CN' | 'COMMON'> {
  if (roles.includes('system_admin') || roles.includes('role_global')) return ['KR', 'CN', 'COMMON'];
  if (roles.includes('role_admin_kr')) return ['KR', 'COMMON'];
  if (roles.includes('role_admin_cn')) return ['CN', 'COMMON'];
  if (roles.includes('role_hr') || roles.includes('role_exec')) {
    if (companyCode === 'CN') return ['CN', 'COMMON'];
    if (companyCode === 'KR') return ['KR', 'COMMON'];
  }
  return [];
}

// ===== 라벨 매핑 =====

const DEDUCTION_TYPE_LABELS: Record<DeductionType, string> = {
  annual: '연차 차감',
  compensatory: '대체휴무 차감',
  none: '차감 없음',
};

const APPROVAL_FLOW_LABELS: Record<ApprovalFlowType, string> = {
  org: '조직 승인',
  orgAndHr: '조직 + 인사팀 승인',
};

const LEAVE_UNIT_LABELS: Record<LeaveUnit, string> = {
  fullDay: '전일',
  halfDay: '반차',
  morningHalf: '오전반차',
  afternoonHalf: '오후반차',
  hourly: '시간단위',
};

// ===== 메인 컴포넌트 =====

export function HrLeaveSettingsPage() {
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [filteredTypes, setFilteredTypes] = useState<LeaveType[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingType, setEditingType] = useState<LeaveType | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<LeaveType | null>(null);

  // V60: role_exec 읽기 전용 처리
  const { hasPermission } = useAuthorization();
  const canManageHr = hasPermission('hr.manage');

  // V68: 호출자 권한 기준 visibilityScope 옵션 결정 (모달 select option / default)
  const { user } = useCurrentUser();
  const allowedScopes = useMemo(
    () => computeAllowedScopesFE(user?.roles || [], user?.profile?.companyCode),
    [user?.roles, user?.profile?.companyCode],
  );

  const {
    fetchLeaveTypes,
    createLeaveType,
    updateLeaveType,
    toggleActive,
    deleteLeaveType,
    isLoading,
    error,
  } = useLeaveTypeMutations();

  // 데이터 로드
  const loadData = useCallback(async () => {
    try {
      const data = await fetchLeaveTypes();
      setLeaveTypes(data);
      setFilteredTypes(data);
    } catch (err) {
      console.error('휴가 유형 로드 실패:', err);
    }
  }, [fetchLeaveTypes]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // 검색 필터
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTypes(leaveTypes);
      return;
    }

    const query = searchQuery.toLowerCase();
    setFilteredTypes(
      leaveTypes.filter(
        (type) =>
          type.code.toLowerCase().includes(query) ||
          type.name.toLowerCase().includes(query)
      )
    );
  }, [searchQuery, leaveTypes]);

  // 생성/수정 핸들러
  const handleSave = async (input: LeaveTypeInput) => {
    try {
      if (editingType) {
        // 수정
        await updateLeaveType({
          code: editingType.code,
          updates: {
            name: input.name,
            description: input.description,
            isPaid: input.isPaid,
            allowedUnits: input.allowedUnits,
            deductionType: input.deductionType,
            approvalFlow: input.approvalFlow,
            requiresAttachment: input.requiresAttachment,
            requiresReason: input.requiresReason,
            isActive: input.isActive,
            visibilityScope: input.visibilityScope, // V68
            specialRules: input.specialRules,
          },
        });
      } else {
        // 생성
        await createLeaveType(input);
      }

      setShowModal(false);
      setEditingType(null);
      await loadData();
    } catch (err: any) {
      alert(err.message || '저장 실패');
    }
  };

  // 활성/비활성 토글
  const handleToggleActive = async (type: LeaveType) => {
    try {
      await toggleActive(type.code);
      await loadData();
    } catch (err: any) {
      alert(err.message || '상태 변경 실패');
    }
  };

  // 삭제 핸들러
  const handleDelete = async (type: LeaveType) => {
    try {
      await deleteLeaveType(type.code);
      setShowDeleteConfirm(null);
      await loadData();
    } catch (err: any) {
      alert(err.message || '삭제 실패');
    }
  };

  return (
    <div>
      {/* 페이지 제목 */}
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          color: 'var(--color-gray-800)',
          marginBottom: '8px',
        }}
      >
        휴가 설정
      </h1>
      <p style={{ color: 'var(--color-gray-500)', marginBottom: '24px' }}>
        휴가 유형을 관리합니다. 코드는 생성 후 변경할 수 없습니다.
      </p>

      {/* 상단 컨트롤 */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* 좌측: 추가 버튼 + 새로고침 */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {canManageHr && (
            <button
              onClick={() => {
                setEditingType(null);
                setShowModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '8px 14px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <Plus size={16} />
              휴가 유형 추가
            </button>
          )}

          <button
            onClick={loadData}
            disabled={isLoading}
            style={{
              padding: '8px',
              background: 'var(--color-gray-100)',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '6px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            title="새로고침"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* 우측: 검색창 */}
        <div style={{ position: 'relative' }}>
          <Search
            size={16}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-gray-400)',
            }}
          />
          <input
            type="text"
            placeholder="코드 또는 이름 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px 8px 36px',
              borderRadius: '6px',
              border: '1px solid var(--color-gray-300)',
              fontSize: '14px',
              width: '240px',
            }}
          />
        </div>
      </div>

      {/* 에러 표시 */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            background: 'var(--color-red-50)',
            color: 'var(--color-red-600)',
            borderRadius: '8px',
            marginBottom: '16px',
          }}
        >
          {error.message}
        </div>
      )}

      {/* 테이블 */}
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        {/* 테이블 헤더 - V5.14: 코드 컬럼 너비 증가 (120px → 180px) */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: canManageHr ? '180px 150px 1fr 120px 120px 90px 100px 120px' : '180px 150px 1fr 120px 120px 90px 100px',
            gap: '8px',
            padding: '12px 16px',
            background: 'var(--color-gray-50)',
            borderBottom: '1px solid var(--color-gray-200)',
            fontWeight: '600',
            fontSize: '13px',
            color: 'var(--color-gray-600)',
          }}
        >
          <div>코드</div>
          <div>이름</div>
          <div>설명</div>
          <div>차감 유형</div>
          <div>승인 플로우</div>
          <div>노출 범위</div>{/* V68 */}
          <div>상태</div>
          {canManageHr && <div style={{ textAlign: 'center' }}>관리</div>}
        </div>

        {/* 로딩 */}
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            로딩 중...
          </div>
        )}

        {/* 데이터 없음 */}
        {!isLoading && filteredTypes.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--color-gray-500)' }}>
            {searchQuery ? '검색 결과가 없습니다.' : '등록된 휴가 유형이 없습니다.'}
          </div>
        )}

        {/* 데이터 행 */}
        {!isLoading &&
          filteredTypes.map((type) => (
            <div
              key={type.code}
              style={{
                display: 'grid',
                gridTemplateColumns: canManageHr ? '180px 150px 1fr 120px 120px 90px 100px 120px' : '180px 150px 1fr 120px 120px 90px 100px',
                gap: '8px',
                padding: '12px 16px',
                borderBottom: '1px solid var(--color-gray-100)',
                alignItems: 'center',
                fontSize: '14px',
                color: type.isActive ? 'var(--color-gray-800)' : 'var(--color-gray-400)',
                background: type.isActive ? 'white' : 'var(--color-gray-50)',
              }}
            >
              {/* V5.14: 코드 컬럼 ellipsis 추가 */}
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={type.code}
              >
                {type.code}
              </div>
              <div style={{ fontWeight: '500' }}>{type.name}</div>
              <div
                style={{
                  color: 'var(--color-gray-500)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                title={type.description}
              >
                {type.description || '-'}
              </div>
              <div>{DEDUCTION_TYPE_LABELS[type.deductionType]}</div>
              <div>{APPROVAL_FLOW_LABELS[type.approvalFlow]}</div>
              {/* V68: 노출 범위 — 미지정(legacy)은 'KR' 로 표시 */}
              <div>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background:
                      (type.visibilityScope || 'KR') === 'COMMON' ? 'var(--color-blue-100)'
                      : (type.visibilityScope || 'KR') === 'CN' ? 'var(--color-purple-100)'
                      : 'var(--color-gray-100)',
                    color:
                      (type.visibilityScope || 'KR') === 'COMMON' ? 'var(--color-blue-800)'
                      : (type.visibilityScope || 'KR') === 'CN' ? 'var(--color-purple-800)'
                      : 'var(--color-gray-700)',
                  }}
                >
                  {(type.visibilityScope || 'KR') === 'COMMON' ? '공통'
                    : (type.visibilityScope || 'KR') === 'CN' ? 'CN' : 'KR'}
                </span>
              </div>
              <div>
                <span
                  style={{
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: type.isActive ? 'var(--color-green-100)' : 'var(--color-gray-200)',
                    color: type.isActive ? 'var(--color-green-800)' : 'var(--color-gray-600)',
                  }}
                >
                  {type.isActive ? '활성' : '비활성'}
                </span>
              </div>
              {/* V60: role_exec는 관리 버튼 숨김 */}
              {canManageHr && (
                <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                  {/* 수정 */}
                  <button
                    onClick={() => {
                      setEditingType(type);
                      setShowModal(true);
                    }}
                    style={{
                      padding: '6px',
                      background: 'var(--color-gray-100)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    title="수정"
                  >
                    <Edit2 size={14} />
                  </button>

                  {/* 활성/비활성 토글 */}
                  <button
                    onClick={() => handleToggleActive(type)}
                    style={{
                      padding: '6px',
                      background: type.isActive ? 'var(--color-orange-100)' : 'var(--color-green-100)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    title={type.isActive ? '비활성화' : '활성화'}
                  >
                    {type.isActive ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  </button>

                  {/* 삭제 */}
                  <button
                    onClick={() => setShowDeleteConfirm(type)}
                    style={{
                      padding: '6px',
                      background: 'var(--color-red-100)',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                    }}
                    title="삭제"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
      </div>

      {/* 생성/수정 모달 */}
      {showModal && (
        <LeaveTypeFormModal
          leaveType={editingType}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingType(null);
          }}
          isLoading={isLoading}
          allowedScopes={allowedScopes}
        />
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          leaveType={showDeleteConfirm}
          onConfirm={() => handleDelete(showDeleteConfirm)}
          onClose={() => setShowDeleteConfirm(null)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}

// ===== 폼 모달 =====

interface LeaveTypeFormModalProps {
  leaveType: LeaveType | null;
  onSave: (input: LeaveTypeInput) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  /** V68: 호출자 권한 기준 visibilityScope 허용 옵션 목록 */
  allowedScopes: Array<'KR' | 'CN' | 'COMMON'>;
}

function LeaveTypeFormModal({ leaveType, onSave, onClose, isLoading, allowedScopes }: LeaveTypeFormModalProps) {
  const isEdit = !!leaveType;

  const [code, setCode] = useState(leaveType?.code || '');
  const [name, setName] = useState(leaveType?.name || '');
  const [description, setDescription] = useState(leaveType?.description || '');
  const [isPaid, setIsPaid] = useState(leaveType?.isPaid ?? true);
  // V282-1: 빈 배열도 방어 — 단일 정책 상수 사용
  const [allowedUnits, setAllowedUnits] = useState<LeaveUnit[]>(
    leaveType?.allowedUnits?.length ? leaveType.allowedUnits : DEFAULT_ALLOWED_UNITS
  );
  const [deductionType, setDeductionType] = useState<DeductionType>(leaveType?.deductionType || 'none');
  const [approvalFlow, setApprovalFlow] = useState<ApprovalFlowType>(leaveType?.approvalFlow || 'org');
  const [requiresAttachment, setRequiresAttachment] = useState(leaveType?.requiresAttachment || false);
  const [requiresReason, setRequiresReason] = useState(leaveType?.requiresReason || false);
  const [isActive, setIsActive] = useState(leaveType?.isActive ?? true);
  // V68: 노출 범위 초기값 — 편집 시 기존 doc 값 우선. 신규 시 호출자 허용 옵션 중 첫 번째 (KR > CN > COMMON 우선순위).
  const computeInitialScope = (): 'KR' | 'CN' | 'COMMON' => {
    const existing = leaveType?.visibilityScope;
    if (existing === 'KR' || existing === 'CN' || existing === 'COMMON') {
      // 편집 모드: 기존 값 유지 (allowedScopes 외라도 표시 — BE 가 차단)
      return existing;
    }
    // 신규 모드: 호출자 권한 기준 default
    if (allowedScopes.includes('KR')) return 'KR';
    if (allowedScopes.includes('CN')) return 'CN';
    if (allowedScopes.includes('COMMON')) return 'COMMON';
    return 'KR';
  };
  const [visibilityScope, setVisibilityScope] = useState<'KR' | 'CN' | 'COMMON'>(computeInitialScope());

  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!code.trim() || !name.trim()) {
      setSubmitError('코드와 이름은 필수입니다.');
      return;
    }

    // V281: 사용 가능 단위 필수 검증
    if (allowedUnits.length === 0) {
      setSubmitError('사용 가능 단위를 1개 이상 선택해주세요.');
      return;
    }

    if (!isEdit && !/^[a-z][a-z0-9_]*$/.test(code)) {
      setSubmitError('코드는 영문 소문자로 시작하고 영문, 숫자, 언더스코어만 사용 가능합니다.');
      return;
    }

    try {
      await onSave({
        code,
        name,
        description,
        isPaid,
        allowedUnits,
        deductionType,
        approvalFlow,
        requiresAttachment,
        requiresReason,
        isActive,
        visibilityScope, // V68
      });
    } catch (err: any) {
      setSubmitError(err.message);
    }
  };

  const handleUnitToggle = (unit: LeaveUnit) => {
    setAllowedUnits((prev) =>
      prev.includes(unit)
        ? prev.filter((u) => u !== unit)
        : [...prev, unit]
    );
  };

  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px',
            borderBottom: '1px solid var(--color-gray-200)',
          }}
        >
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-gray-800)', margin: 0 }}>
            {isEdit ? '휴가 유형 수정' : '휴가 유형 추가'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
          {/* 코드 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              코드 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              disabled={isEdit}
              placeholder="예: annual, compensatory"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
                fontFamily: 'monospace',
                background: isEdit ? 'var(--color-gray-100)' : 'white',
              }}
            />
            {!isEdit && (
              <p style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '4px' }}>
                영문 소문자로 시작, 영문/숫자/언더스코어만 가능 (생성 후 변경 불가)
              </p>
            )}
          </div>

          {/* 이름 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              이름 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="예: 연차휴가, 대체휴무"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
              }}
            />
          </div>

          {/* 설명 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              설명
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="간단한 설명"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
              }}
            />
          </div>

          {/* 차감 유형 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              차감 유형
            </label>
            <select
              value={deductionType}
              onChange={(e) => setDeductionType(e.target.value as DeductionType)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
              }}
            >
              <option value="annual">연차 차감</option>
              <option value="compensatory">대체휴무 차감</option>
              <option value="none">차감 없음</option>
            </select>
          </div>

          {/* 승인 플로우 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              승인 플로우
            </label>
            <select
              value={approvalFlow}
              onChange={(e) => setApprovalFlow(e.target.value as ApprovalFlowType)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
              }}
            >
              <option value="org">조직 승인만</option>
              <option value="orgAndHr">조직 + 인사팀 승인</option>
            </select>
          </div>

          {/* V68: 노출 범위 (휴가 신청 화면 회사별 필터). 호출자 권한 기준 옵션만 렌더. */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '4px', color: 'var(--color-gray-700)' }}>
              노출 범위 *
            </label>
            <select
              value={visibilityScope}
              onChange={(e) => setVisibilityScope(e.target.value as 'KR' | 'CN' | 'COMMON')}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid var(--color-gray-300)',
                fontSize: '14px',
              }}
            >
              {/* 편집 모드에서 기존 scope 가 allowedScopes 외이면 disabled option 으로 표시 (BE 가 변경 차단) */}
              {(['KR', 'CN', 'COMMON'] as const)
                .filter((s) => allowedScopes.includes(s) || (isEdit && s === visibilityScope))
                .map((s) => (
                  <option key={s} value={s} disabled={!allowedScopes.includes(s)}>
                    {s === 'KR' ? 'KR — 한국 직원만'
                      : s === 'CN' ? 'CN — 중국 직원만'
                      : '공통 — KR/CN 모두'}
                    {!allowedScopes.includes(s) ? ' (수정 권한 없음)' : ''}
                  </option>
                ))}
            </select>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--color-gray-500)' }}>
              직원 휴가 신청 폼에서 본인 회사 코드에 해당하는 휴가만 노출됩니다. (차감/발생/승인 로직과 무관)
            </p>
          </div>

          {/* 사용 단위 */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', marginBottom: '8px', color: 'var(--color-gray-700)' }}>
              사용 가능 단위 <span style={{ color: 'var(--color-red-500)' }}>*</span>
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {(Object.keys(LEAVE_UNIT_LABELS) as LeaveUnit[]).map((unit) => (
                <label
                  key={unit}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '6px 12px',
                    background: allowedUnits.includes(unit) ? 'var(--color-primary-50)' : 'var(--color-gray-100)',
                    border: allowedUnits.includes(unit) ? '1px solid var(--color-primary)' : '1px solid var(--color-gray-300)',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    fontSize: '13px',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={allowedUnits.includes(unit)}
                    onChange={() => handleUnitToggle(unit)}
                    style={{ display: 'none' }}
                  />
                  {LEAVE_UNIT_LABELS[unit]}
                </label>
              ))}
            </div>
          </div>

          {/* 옵션들 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isPaid}
                onChange={(e) => setIsPaid(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>유급 휴가</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={requiresAttachment}
                onChange={(e) => setRequiresAttachment(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>첨부파일 필수</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={requiresReason}
                onChange={(e) => setRequiresReason(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>사유 필수</span>
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span style={{ fontSize: '14px' }}>활성화</span>
            </label>
          </div>

          {/* 에러 */}
          {submitError && (
            <div
              style={{
                padding: '12px',
                background: 'var(--color-red-50)',
                color: 'var(--color-red-600)',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            >
              {submitError}
            </div>
          )}

          {/* 버튼 */}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 20px',
                background: 'var(--color-gray-100)',
                color: 'var(--color-gray-700)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '10px 20px',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1,
              }}
            >
              {isLoading ? '저장 중...' : isEdit ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  );
}

// ===== 삭제 확인 모달 =====

interface DeleteConfirmModalProps {
  leaveType: LeaveType;
  onConfirm: () => void;
  onClose: () => void;
  isLoading: boolean;
}

function DeleteConfirmModal({ leaveType, onConfirm, onClose, isLoading }: DeleteConfirmModalProps) {
  return (
    <ModalOverlay
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1001,
      }}
      onClose={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '100%',
          maxWidth: '400px',
          padding: '24px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '12px', color: 'var(--color-gray-800)' }}>
          휴가 유형 삭제
        </h3>
        <p style={{ color: 'var(--color-gray-600)', marginBottom: '8px' }}>
          <strong>'{leaveType.name}'</strong> ({leaveType.code})을(를) 삭제하시겠습니까?
        </p>
        <p style={{ color: 'var(--color-orange-600)', fontSize: '13px', marginBottom: '20px' }}>
          이미 휴가 신청에 사용된 유형은 삭제할 수 없습니다. 비활성화를 권장합니다.
        </p>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 20px',
              background: 'var(--color-gray-100)',
              color: 'var(--color-gray-700)',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            style={{
              padding: '10px 20px',
              background: 'var(--color-red-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? '삭제 중...' : '삭제'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
